import React, { useEffect, useMemo, useRef, useState } from "react";
import { Model, settings } from "survey-core";
import { Survey } from "survey-react-ui";
import {
  confirmActionAsyncFunc,
  markdownHandler,
} from "../../components/surveyjs/survey/survey.util";
import { initSurvey } from "../../components/surveyjs/survey/init-survey";
import { getI18n, useTranslation } from "react-i18next";
import Icon from "../../../../components/icon/icon";
import { SvgAssets } from "../../../../components/icon/assets";
import { useQuery } from "../../../../utils/hooks/use-query";
import { PruToast } from "../../../../components/pru-toast";
import { useNavigate } from "react-router-dom";
import { CandidateDataTemplate, TemplateField } from "./template";
import {
  fetchGlobalConfig,
  getInvitationStatus,
  inviteCandidate,
  inviteCandidateEdit,
} from "../../network/network";
import { ONBOARDING_PATH, ROOT_PATH } from "../../../../routes/constants";
import { cloneDeep } from "lodash";
import { getFullRegionList, getRecruitPublicConfig } from "../../../common/network/network";
import { getIdTypeOptions } from "../../../../utils/common-utils";
import { GlobalHelper } from "../../../../utils/helpers/global-helper";
import { NameCompositionEnum } from "../../../../constants/constants";
let localLangCode: string;
const elements = CandidateDataTemplate.pages[0].elements;
export const CandidateDataFormScreen: React.FC = () => {
  initSurvey();
  const { t } = useTranslation();
  const query: any = useQuery();
  const navigate = useNavigate();
  const [invitationType, setInvitationType] = useState("new");
  const [formValues, setForm] = useState({} as any);
  const [globalConfigs, setGlobalConfigs] = useState({} as any);
  const [recruitPublicConfigs, setRecruitPublicConfigs] = useState({} as any);
  const inviteId = query.get("iid");
  const agentCodeInPath = query.get("code");
  const idFields = useMemo(() => {
    const idTypeConfigs = globalConfigs?.region?.idType;
    if (recruitPublicConfigs?.disableCandidateDataIdDocCheck || !idTypeConfigs) {
      return [];
    }
    const options = getIdTypeOptions(globalConfigs?.region?.idType)?.options;
    return [
      {
        type: "dropdown",
        name: "idType",
        title: t("Common.id_type"),
        isRequired: true,
        choices: options ?? [],
        requiredErrorText: t("Common.please_select_id_type"),
        readOnly: invitationType === "edit",
      },
      ...Object.keys(idTypeConfigs).map((key, index) => {
        const option = idTypeConfigs[key];
        return {
          type: "text",
          name: `idNum`,
          visibleIf: `{idType}="${option.value}"`,
          title: t("Common.id_num"),
          isRequired: true,
          tooltips: option.desc ? t(option.desc.replace(":", ".")) : "",
          placeholder: t("Common.id_num_nric_placeholder"),
          readOnly: invitationType === "edit",
          validators:
            invitationType !== "edit"
              ? [
                  {
                    type: "expression",
                    text: t(option.errMsg.replace(":", ".")),
                    expression: "idNumValidate({#idType},{idNum})",
                  },
                ]
              : [],
          inputType: "text",
          requiredErrorText: t("Common.id_num_nric_placeholder"),
        };
      }),
    ];
  }, [globalConfigs, recruitPublicConfigs, invitationType]);
  useEffect(() => {
    if (!inviteId) {
      PruToast({ message: "Wrong url, please check" });
      return;
    }
    const getInvitationStatusPromise = getInvitationStatus(inviteId);
    Promise.all([
      getInvitationStatusPromise,
      fetchGlobalConfig(),
      getRecruitPublicConfig(),
      getFullRegionList(),
    ]).then((reses: any) => {
      const [res, globalConfigRes, recruitPublicConfigRes, regionConfigs] = reses;
      const regionPhoneField = elements.find((item: any) => item.name === "regionPhone");
      if (regionPhoneField?.templateElements?.[0] && regionConfigs) {
        const regionOpts = Object.values(regionConfigs).map((region: any) => ({
          text: `${region.code}`,
          key: region.code,
          value: region.code,
          reg: region.regex,
        }));
        // @ts-ignore
        regionPhoneField.templateElements[0].choices = regionOpts;
        setForm({
          regionCode: regionOpts?.[0].key ?? "+852",
        });
      } else {
        setForm({
          regionCode: "+852",
        });
      }
      setGlobalConfigs(globalConfigRes.data?.content);
      GlobalHelper.setIdTypeConfigs(globalConfigRes.data?.content?.region?.idType);
      setRecruitPublicConfigs(recruitPublicConfigRes.Recruitment);
      const {
        submitted,
        expired,
        deleted,
        phone,
        firstname,
        lastname,
        agentCode,
        gender,
        identityDocumentType: idType,
        identityDocumentNumber: idNum,
        email,
        invitationType = "new",
        languagePreference,
        languagePreferenceOps = '[{"code":"zh-Hant","name":"繁體中文","fallBackCode":"zh-Hant"},{"code":"en","name":"English","fallBackCode":"zh-Hant"}]',
      } = res?.data || res || {};
      setInvitationType(invitationType);

      if (submitted) {
        return navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/join-done`);
      } else if (expired) {
        return navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/join-expired`);
      } else if (deleted) {
        return navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/invitation-cancelled`);
      }
      if (!phone) {
        PruToast({
          message: t("Invalid Phone Number"),
        });
        return;
      }
      const languagePreferenceField: any = elements.find(
        (item) => item.name === "languagePreference",
      );
      const languagePreferenceOptions = JSON.parse(languagePreferenceOps);
      const choicesFromApi = languagePreferenceOptions.map((opt: any) => ({
        value: opt.code,
        text: opt.name,
      }));
      languagePreferenceField.choices = choicesFromApi.concat(
        languagePreferenceField.choices.filter(
          (choice: { value: any }) =>
            !choicesFromApi.find((_choice: { value: any }) => _choice.value === choice.value),
        ),
      );
      const arrPhone = phone.split(" ");
      arrPhone.length = 2; // avoid out of range exception when using arrPhone[1]

      if (invitationType === "edit") {
        setForm({
          firstname,
          lastname,
          gender,
          email,
          regionCode: arrPhone[0],
          phoneNumber: arrPhone[1],
          languagePreference,
          agentCode,
          idType,
          idNum,
        });
      } else {
        setForm({
          ...formValues,
          agentCode,
          firstname,
          lastname,
          gender,
          idType:
            idType ?? getIdTypeOptions(globalConfigRes.data?.content?.region?.idType)?.default,
          idNum,
          email,
          regionCode: arrPhone[0],
          phoneNumber: arrPhone[1],
          languagePreference,
        });
      }
    });
  }, []);
  const dataRef = useRef<any>({});
  const form = useMemo(() => {
    if (!formValues.regionCode || !formValues.phoneNumber) {
      return null;
    }
    const insertedIdFields = elements.find((element) => element.name === TemplateField.idType);
    const needDelete =
      insertedIdFields || (invitationType === "edit" && !formValues.idNum) ? idFields.length : 0;
    elements.splice(3, needDelete, ...(idFields as any));
    const candidateFullnameComposition = recruitPublicConfigs?.candidateFullnameComposition;
    if (
      candidateFullnameComposition &&
      !candidateFullnameComposition.includes(NameCompositionEnum.lastName)
    ) {
      const index = elements.findIndex((ele) => ele.name === NameCompositionEnum.lastName);
      if (index > -1) {
        elements[index - 1].title = "Common.fullname";
        elements.splice(index, 1);
      }
    }
    convertTemplate(CandidateDataTemplate);
    const survey = new Model(cloneDeep(CandidateDataTemplate));
    survey.showPageTitles = false;
    survey.showNavigationButtons = "none";
    survey.questionErrorLocation = "bottom";
    survey.showCompletedPage = false;
    const defaultValue = {
      firstName: formValues.firstname ?? "",
      lastName: formValues.lastname ?? "",
      gender: formValues.gender,
      email: formValues.email ?? "",
      idType: formValues.idType ?? "",
      idNum: formValues.idNum ?? "",
      languagePreference: formValues.languagePreference,
      regionPhone: [
        {
          regionCode: formValues.regionCode ?? "",
          phoneNumber: formValues.phoneNumber,
        },
      ],
    };
    survey.mergeData(defaultValue);
    survey.onComplete.add((_sender: any) => {});
    survey.onTextMarkdown.add(markdownHandler);
    survey.onGetQuestionTitleActions.add((_, options) => {
      if (options.question.tooltips) {
        options.titleActions = [
          {
            id: "show-popover",
            component: "popover",
            tooltip: options.question.tooltips,
          },
        ];
      }
    });
    settings.confirmActionAsync = confirmActionAsyncFunc;
    return (
      <div className="flex flex-1 flex-col">
        <div className="flex flex-1 flex-col w-screen pb-4">
          <div className=" bg-white w-screen rounded-tr-3xl h-10"></div>
          <Survey currentPageNo={0} className="survey-common" id="onboarding-join" model={survey} />
        </div>

        <div className=" w-screen sticky bottom-0 bg-white px-4 pt-2 pb-7 flex items-center justify-between gap-4" style={{zIndex: 12}}>
          <button
            className="bg-red-500 flex-1 text-white font-semibold py-3  rounded-lg shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75"
            onClick={() => {
              let isValid = false;
              isValid = survey.validateCurrentPage();

              if (!isValid) {
                return;
              }
              const surveyData = survey.data;
              const candidateData: any = {};
              candidateData.inviteId = inviteId;

              candidateData.gender = surveyData.gender ? "M" : "F";
              candidateData.firstname = surveyData.firstName;
              candidateData.lastname = surveyData.lastName;
              candidateData.identityDocumentType = surveyData.idType;
              candidateData.identityDocumentNumber = surveyData.idNum;
              candidateData.email = surveyData.email;
              candidateData.isAgreedPolicy = surveyData.privacyPolicyAgreement;
              candidateData.regionCode = survey.data.regionPhone[0].regionCode;
              candidateData.phoneNumber = survey.data.regionPhone[0].phoneNumber;
              candidateData.isAgreedPolicy = surveyData.privacyPolicyAgreement;
              candidateData.isUpdate = false;
              candidateData.languagePreference = surveyData.languagePreference ?? "en";
              candidateData.agentCode = agentCodeInPath ?? formValues.agentCode;
              invitationType === "new"
                ? inviteCandidate(candidateData, navigate)
                : inviteCandidateEdit(candidateData, navigate);
            }}
          >
            {t("global.text.confirm")}
          </button>
        </div>
      </div>
    );
  }, [formValues, idFields, invitationType, recruitPublicConfigs]);
  return (
    <div className="h-screen w-screen flex flex-col">
      <div className="absolute top-14">
        <h1 className="text-center text-4xl font-bold p-6 text-white">
          {t("Common.candidate_data_screen_title")}
        </h1>
      </div>

      <div className="w-screen h-40">
        <Icon svg={SvgAssets.headerBg} width={"100%"} />
      </div>
      {form}
    </div>
  );
};

const convertTemplate = (template: any) => {
  // Function to translate a given field if it exists
  const translateField = (obj: any, field: string) => {
    if (obj?.[field]) {
      obj[field] = getI18n().t(obj[field]);
    }
  };

  // Loop through each element in the first page
  template.pages[0].elements = template.pages[0].elements.map((q: any) => {
    // Translate title, labelTrue, labelFalse, requiredErrorText fields
    translateField(q, "title");
    translateField(q, "labelTrue");
    translateField(q, "labelFalse");
    translateField(q, "requiredErrorText");

    // Check if the element has validators and translate their text fields
    if (q.validators && Array.isArray(q.validators)) {
      q.validators.forEach((validator: any) => {
        translateField(validator, "text");
      });
    }

    // If the element is a paneldynamic type, we need to handle nested templateElements
    if (q.type === "paneldynamic" && q.templateElements && Array.isArray(q.templateElements)) {
      q.templateElements = q.templateElements.map((te: any) => {
        translateField(te, "title");
        translateField(te, "labelTrue");
        translateField(te, "labelFalse");
        translateField(te, "requiredErrorText");

        if (te.validators && Array.isArray(te.validators)) {
          te.validators.forEach((validator: any) => {
            translateField(validator, "text");
          });
        }

        return te;
      });
    }

    return q;
  });
};
